import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { HiBell, HiChat, HiUser, HiShieldCheck, HiOutlineSparkles, HiClipboardList } from 'react-icons/hi';
import api from '@services/api';
import useAuthStore from '@store/authStore';

const NotificationBell = ({ isScrolled, isHomePage }) => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchNotifications = async () => {
        try {
            const res = await api.get('/notifications');
            if (res.data.success) {
                setNotifications(res.data.data);
                setUnreadCount(res.data.unreadCount);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000); // every minute
        return () => clearInterval(interval);
    }, []);

    const markAsRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/read-all');
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.isRead) {
            markAsRead(notification.id);
        }
        setShowNotifications(false);
        if (notification.type === 'MESSAGE') {
            navigate('/dashboard/messages', { state: { openMessageId: notification.relatedData } });
        } else if (notification.type === 'NEW_USER') {
            const rData = typeof notification.relatedData === 'string' ? JSON.parse(notification.relatedData) : notification.relatedData;
            navigate('/dashboard/admin/users', { state: { highlightUserId: rData?.userId || rData } });
        } else if (notification.type === 'ASSESSOR_APPLICATION' || (notification.title && notification.title.toLowerCase().includes('assessor'))) {
            const appDataRaw = notification.relatedData;
            let appId = null;
            if (appDataRaw) {
                if (typeof appDataRaw === 'string') {
                    try {
                        const parsed = JSON.parse(appDataRaw);
                        appId = parsed.applicationId || parsed.id;
                    } catch (e) {
                        appId = appDataRaw; // Fallback to raw string if it's just the ID
                    }
                } else if (typeof appDataRaw === 'object') {
                    appId = appDataRaw.applicationId || appDataRaw.id;
                }
            }
            console.log('Redirecting to Assessor Recruitments with ID:', appId);
            navigate('/dashboard/admin/assessor-recruitments', { state: { openAppId: appId } });
        } else if (notification.type === 'AUDIT') {
            const lowerTitle = (notification.title || '').toLowerCase();
            const lowerContent = (notification.content || '').toLowerCase();
            const isVendorPickingAssessor = (user?.role !== 'assessor') && (
                (lowerTitle.includes('select') && lowerTitle.includes('assessor')) || 
                (lowerContent.includes('select') && lowerContent.includes('assessor'))
            );

            if (isVendorPickingAssessor) {
                navigate('/dashboard/assessors-directory');
            } else if (notification.relatedData) {
                let rData = notification.relatedData;
                if (typeof rData === 'string' && (rData.startsWith('{') || rData.startsWith('['))) {
                    try { rData = JSON.parse(rData); } catch (e) { /* use raw */ }
                }
                
                const auditId = rData?.auditId || (typeof rData === 'number' || typeof rData === 'string' ? rData : null);
                
                if (auditId && typeof auditId !== 'object') {
                    if (user?.role === 'assessor') {
                        navigate(`/dashboard/assessor?highlightId=${auditId}`);
                    } else {
                        navigate(`/dashboard/audits/${auditId}`);
                    }
                }
            } else {
                navigate(user?.role === 'assessor' ? '/dashboard/assessor' : '/dashboard/audits');
            }
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'MESSAGE': return <HiChat className="w-5 h-5 text-blue-500" />;
            case 'NEW_USER': return <HiUser className="w-5 h-5 text-green-500" />;
            case 'AUDIT': return <HiShieldCheck className="w-5 h-5 text-gold-500" />;
            case 'PAYMENT': return <HiOutlineSparkles className="w-5 h-5 text-purple-500" />;
            case 'ASSESSOR_APPLICATION': return <HiClipboardList className="w-5 h-5 text-primary-500" />;
            default: return <HiBell className="w-5 h-5 text-secondary-400" />;
        }
    };

    const isLightMode = isHomePage && !isScrolled;

    return (
        <div className="relative">
            <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative p-2 rounded-lg transition-colors focus:outline-none ${isLightMode ? 'text-white hover:text-gold-400' : 'text-secondary-600 hover:text-gold-600'} hover:bg-white/10`}
            >
                <HiBell className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-red-500 rounded-full px-1 border-2 border-transparent">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 max-h-[80vh] flex flex-col bg-white rounded-xl shadow-2xl border border-secondary-200 animate-scale-in z-50">
                    <div className="flex items-center justify-between p-4 border-b border-secondary-100 bg-secondary-50/50 rounded-t-xl">
                        <h3 className="font-bold text-secondary-900">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>
                    <div className="overflow-y-auto flex-1 p-2 bg-white">
                        {notifications.length === 0 ? (
                            <div className="p-4 text-sm text-secondary-500 text-center flex flex-col items-center justify-center space-y-2">
                                <HiBell className="w-8 h-8 text-secondary-300" />
                                <p>No notifications yet</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors flex gap-3
                                            ${notification.isRead ? 'bg-white hover:bg-secondary-50' : 'bg-primary-50/50 hover:bg-primary-50'}
                                        `}
                                    >
                                        <div className={`mt-0.5 p-2 rounded-full h-fit ${
                                            notification.type === 'AUDIT' ? 'bg-gold-50' : 
                                            notification.type === 'MESSAGE' ? 'bg-blue-50' : 
                                            notification.type === 'NEW_USER' ? 'bg-green-50' : 
                                            notification.type === 'ASSESSOR_APPLICATION' ? 'bg-primary-50' : 'bg-secondary-100'
                                        }`}>
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start mb-1">
                                                <p className={`text-sm truncate pr-2 ${notification.isRead ? 'font-medium text-secondary-700' : 'font-bold text-secondary-900'}`}>
                                                    {notification.title}
                                                </p>
                                                {!notification.isRead && (
                                                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-1.5" />
                                                )}
                                            </div>
                                            <p className="text-xs text-secondary-500 line-clamp-2 leading-relaxed">
                                                {notification.content}
                                            </p>
                                            <p className="text-[10px] text-secondary-400 mt-2">
                                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
